import React, { useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import classNames from 'classnames';
import type { AdConfig, AdSchedule } from '@jwp/ott-common/types/ad-schedule';
import type { PlaylistItem } from '@jwp/ott-common/types/playlist';
import { useConfigStore } from '@jwp/ott-common/src/stores/ConfigStore';
import { deepCopy } from '@jwp/ott-common/src/utils/collection';
import { testId } from '@jwp/ott-common/src/utils/common';
import { logInfo } from '@jwp/ott-common/src/logger';
import useEventCallback from '@jwp/ott-hooks-react/src/useEventCallback';
import { useMediaSources } from '@jwp/ott-hooks-react/src/useMediaSources';
import env from '@jwp/ott-common/src/env';

import type { JWPlayer } from '../../../types/jwplayer';
import { addScript } from '../../utils/dom';
import type { PlayerEvents, PlayerHandle } from '../../types/player';
import createInjectableComponent from '../../modules/createInjectableComponent';
import useStableHandlers from '../../hooks/useStableHandlers';

import styles from './Player.module.scss';

export const PlayerIdentifier = Symbol('PLAYER');

export type PlayerProps = {
  className?: string;
  feedId?: string;
  item: PlaylistItem;
  startTime?: number;
  autostart?: boolean;
  controls?: boolean;
  adsData?: AdConfig | AdSchedule;
  on?: Partial<PlayerEvents>;
  onPlaylistItemCallback?: (item: PlaylistItem) => Promise<undefined | PlaylistItem>;
  playerHandle?: React.RefObject<PlayerHandle | null>;
};

const Player: React.FC<PlayerProps> = ({
  className,
  item,
  adsData,
  on = {},
  onPlaylistItemCallback,
  feedId,
  startTime = 0,
  autostart,
  controls = true,
  playerHandle,
}: PlayerProps) => {
  const playerElementRef = useRef<HTMLDivElement>(null);
  const playerRef = useRef<JWPlayer | undefined>(undefined);
  const loadingRef = useRef(false);
  const backClickRef = useRef(false);
  const [libLoaded, setLibLoaded] = useState(!!window.jwplayer);
  const startTimeRef = useRef(startTime);
  const sources = useMediaSources({ item, baseUrl: env.APP_CONTENT_API_BASE_URL });

  const { settings } = useConfigStore((s) => s);

  const playerId = settings.playerId;
  const playerLicenseKey = settings.playerLicenseKey;

  // This is a copy of all callback props but made stable to prevent unneeded re-renders
  const stableOn = useStableHandlers(on);

  useImperativeHandle(
    playerHandle,
    () => {
      return {
        play: () => playerRef.current?.play(),
        pause: () => playerRef.current?.pause(),
        seek: (position) => playerRef.current?.seek(position),
        getPosition: async () => playerRef.current?.getPosition() || 0,
        getDuration: async () => playerRef.current?.getDuration() || 0,
        getState: async () => playerRef.current?.getState() || 'idle',
        skipAd: () => playerRef.current?.skipAd(),
        setCurrentCaptionTrack: (track: number) => playerRef.current?.setCurrentCaptions(track),
        setCurrentAudioTrack: (track: number) => playerRef.current?.setCurrentAudioTrack(track),
      };
    },
    [],
  );

  const handleFirstFrame = useEventCallback(() => {
    // a11y: autofocus to play/pause button
    const playerContainer = playerRef.current?.getContainer();
    const playPauseButton = playerContainer?.querySelector(
      '.jw-controlbar [aria-label="Play"], .jw-button-container [aria-label="Pause"]',
    ) as HTMLDivElement | null;
    playPauseButton?.focus();
  });

  const handlePlaylistItem = useEventCallback(() => {
    // ensure that the player starts playing when a new item is loaded and autostart is enabled
    // webOS -> the autostart mechanism fails which prevents autoplaying the video
    if (autostart && ['paused', 'idle'].includes(playerRef.current?.getState() || '')) {
      playerRef.current?.play();
    }
  });

  const handleBackClick = useEventCallback(() => {
    backClickRef.current = true;
  });

  const handlePlaylistItemCallback = useEventCallback(onPlaylistItemCallback);

  const attachEvents = useCallback(() => {
    const { time, adPlay, adTime, adError, captions, audioTracks, ...listeners } = stableOn;

    // These listeners are also handled in this component
    playerRef.current?.on('backClick', handleBackClick);
    playerRef.current?.on('firstFrame', handleFirstFrame);
    playerRef.current?.on('playlistItem', handlePlaylistItem);

    // This is not an event, but changes the playlistItem before loading it in the player
    playerRef.current?.setPlaylistItemCallback(handlePlaylistItemCallback);

    // This binds most event listeners that don't have extra parameters
    // @ts-expect-error no concrete events without params
    Object.keys(listeners).forEach((event) => playerRef.current?.on(event, () => listeners[event]({ type: event })));

    // Below are all event listeners that need some kind of parameter mapping
    playerRef.current?.on('time', (event) =>
      time?.({
        type: 'time',
        position: event.position,
        duration: event.duration,
      }),
    );
    playerRef.current?.on('audioTracks', (event) => {
      audioTracks?.({
        type: 'audioTracks',
        tracks: event.tracks.map((track) => ({ ...track, label: track.name || track.language })),
        currentTrack: event.currentTrack,
      });
    });
    playerRef.current?.on('audioTrackChanged', (event) =>
      audioTracks?.({
        type: 'audioTracks',
        tracks: (playerRef.current?.getAudioTracks() || []).map((track) => ({ ...track, label: track.name || track.language })),
        currentTrack: event.currentTrack,
      }),
    );

    playerRef.current?.on('captionsList', (event) => {
      captions?.({
        type: 'captions',
        tracks: event.tracks,
        currentTrack: playerRef.current?.getCurrentCaptions() || -1,
      });
    });

    playerRef.current?.on('captionsChanged', (event) => {
      captions?.({ type: 'captions', tracks: playerRef.current?.getCaptionsList() || [], currentTrack: event.track });
    });

    playerRef.current?.on('adPlay', (event) => {
      adPlay?.({
        type: 'adPlay',
        // @ts-expect-error untyped params
        item: event.sequence ?? 1,
        // @ts-expect-error untyped params
        total: event.podcount ?? 1,
        // @ts-expect-error untyped params
        skipOffset: event.skipoffset || undefined,
      });
    });

    playerRef.current?.on('adTime', (event) =>
      adTime?.({
        type: 'adTime',
        position: event.position,
        duration: event.duration,
      }),
    );

    playerRef.current?.on('adError', () => adError?.({ type: 'adError' }));
  }, [stableOn, handleBackClick, handleFirstFrame, handlePlaylistItem, handlePlaylistItemCallback]);

  const detachEvents = useCallback(() => {
    playerRef.current?.off('all');
  }, []);

  useEffect(() => {
    if (!window.jwplayer && !loadingRef.current) {
      loadingRef.current = true;

      const scriptUrl = `${env.APP_API_BASE_URL}/libraries/${playerId}.js`;

      addScript(scriptUrl).then(() => {
        setLibLoaded(true);
        loadingRef.current = false;
      });
    }
  }, [playerId]);

  useEffect(() => {
    // Update the startTimeRef each time the startTime changes
    startTimeRef.current = startTime;
  }, [startTime]);

  useEffect(() => {
    const loadPlaylist = () => {
      if (!item || !playerRef.current) {
        return;
      }

      const currentItem = playerRef.current?.getPlaylistItem() as unknown as PlaylistItem | null;

      // We already loaded this item
      if (currentItem && currentItem.mediaid === item.mediaid) {
        logInfo('Player', 'Calling loadPlaylist with the same item, check the dependencies');
        return;
      }

      // Update autostart parameter
      if (typeof autostart !== 'undefined') {
        playerRef.current?.setConfig({ autostart });
      }

      // Load new item
      playerRef.current.load([deepCopy({ ...item, starttime: startTimeRef.current, feedid: feedId })]);
    };

    const initializePlayer = () => {
      if (!window.jwplayer || !playerElementRef.current) return;

      playerRef.current = window.jwplayer(playerElementRef.current) as JWPlayer;

      // Player options are untyped
      const playerOptions: { [key: string]: unknown } = {
        advertising: {
          ...adsData,
          // Beta feature
          showCountdown: true,
        },
        timeSlider: {
          showAdMarkers: false,
        },
        aspectratio: false,
        controls,
        displaytitle: false,
        displayHeading: false,
        displaydescription: false,
        floating: {
          mode: 'never',
        },
        height: '100%',
        mute: false,
        playbackRateControls: true,
        pipIcon: 'disabled',
        playlist: [deepCopy({ ...item, starttime: startTimeRef.current, feedid: feedId, sources })],
        repeat: false,
        cast: env.APP_CHROMECAST_APPLICATION_ID ? { appid: env.APP_CHROMECAST_APPLICATION_ID } : {},
        stretching: 'uniform',
        width: '100%',
      };

      // Only set the autostart parameter when it is defined or it will override the player.defaults autostart setting
      if (typeof autostart !== 'undefined') {
        playerOptions.autostart = autostart;
      }

      // Set the license key if provided
      if (playerLicenseKey) {
        playerOptions.key = playerLicenseKey;
      }
      playerRef.current.setup(playerOptions);

      attachEvents();
    };

    if (playerRef.current) {
      return loadPlaylist();
    }

    if (libLoaded) {
      initializePlayer();
    }
  }, [libLoaded, item, detachEvents, attachEvents, playerId, autostart, adsData, playerLicenseKey, sources, feedId, controls]);

  useEffect(() => {
    return () => {
      if (playerRef.current) {
        // Detaching events before component unmount
        detachEvents();
        if (backClickRef.current) {
          backClickRef.current = false;
          return;
        }
        playerRef.current.remove();
        playerRef.current = undefined;
      }
    };
  }, [detachEvents, backClickRef]);

  return (
    <div className={classNames(styles.container, className)} data-testid={testId('player-container')}>
      <div ref={playerElementRef} />
    </div>
  );
};

export default createInjectableComponent(PlayerIdentifier, Player);
